import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { usePrevious } from "../../../utilities/hooks";
import { IE11, MS_EDGE, FIREFOX } from "../../../constants";
import { SCROLL_SPEED } from "../../../constants/config/table";
import breakpoints from "../../../constants/breakpoints";
import "./style.css";
import { alreadyDismissed, LOCALSTORAGE_KEY } from "./config";

/**
 * @name Scroll
 * @function
 * @description
 */
function Scroll(props) {
	const [listeners, setListeners] = useState(false);
	const scrollRef = useRef(null);
	const prevParts = usePrevious(props.parts);
	const prevParameters = usePrevious(props.parameters);
	const KEY_RIGHT = IE11 || MS_EDGE ? "Right" : "ArrowRight";
	const KEY_LEFT = IE11 || MS_EDGE ? "Left" : "ArrowLeft";
	const [ helpVisible, setHelpVisible ] = useState( !alreadyDismissed );
	
	/**
	 * Handles the scroll event for horizontal scrolling.
	 *
	 * @param {Event} e - The scroll event.
	 * @returns {void}
	 */
	const handleHorizontalScroll = useCallback((e) =>  {
		let queryValue = ".pst_table th:nth-child(n+3), .pst_table td:nth-child(n+3)";

		// in tablet view we don't have 1st column.
		if (window.innerWidth >= breakpoints.tablet.min && window.innerWidth <= breakpoints.tablet.max) {
			queryValue = ".pst_table th:nth-child(n+2), .pst_table td:nth-child(n+2)";
		}
		const scrollableColumns = document.querySelectorAll(queryValue);
		const left = scrollRef.current ? scrollRef.current.scrollLeft : 0;
		props.setLeftScroll(left);

		scrollableColumns.forEach((column) => {
			column.style.transform = `translateX(-${left}px)`;
		});

		window.requestAnimationFrame(() => {
			const value = `-${left}px`;

			scrollableColumns.forEach((column) => {
				column.style.left = value;
			});
		});
	}, []);

	/**
	 * Handles dismissing of the helper text
	 * @param {Event} e 
	 */
	const handleDismiss = ( e ) => {
		e.preventDefault();
		localStorage.setItem( LOCALSTORAGE_KEY, true );
		setHelpVisible( false );
	};

	/**
	 * Effect hook to set up event listeners for horizontal scrolling, keyboard input, and wheel events.
	 * It also includes handling for IE11-specific vertical scrolling.
	 *
	 * @effect
	 * @returns {void}
	 */
	useEffect(() => {
		const handleScroll = (e) => {
			const { deltaX, deltaY, type } = e,
				isScrollEvent = type === 'scroll',
				isHorizontalTrackpadScroll = type === 'wheel' && Math.abs( deltaX ) > Math.abs( deltaY );

			if ( isScrollEvent || isHorizontalTrackpadScroll ) {
				e.preventDefault();
				handleHorizontalScroll(e);
			}
		};

		const handleTrackpad = (e) => {
			if (scrollRef.current) {
				scrollRef.current.scrollLeft += e.deltaX;
			}
		};

		const handleVerticalScroll = () => {
			const bottom = window.innerHeight || document.documentElement.clientHeight;
			const ref = document.querySelector("nav.pagination__nav");
			const rect = ref ? ref.getBoundingClientRect() : {};

			if (rect.top > bottom) return scrollRef.current.removeAttribute("style");

			scrollRef.current.style.top = `${ rect.top - scrollRef.current.offsetHeight }px`;
		};

		const watchKeyDown = (e) => {
			const key = e.code || e.key;
			const left = scrollRef.current ? scrollRef.current.scrollLeft : 0;

			if (
				(key !== KEY_LEFT && key !== KEY_RIGHT) ||
				(e.target && e.target.tagName === "INPUT")
			) return;

			let pos = 0;

			switch (key) {
				case KEY_LEFT:
				pos = left - SCROLL_SPEED;
				if (pos < 0) pos = 0;
				break;
				case KEY_RIGHT:
				pos = left + SCROLL_SPEED;
				break;
				default:
				pos = 0;
				break;
			}

			scrollRef.current.scrollLeft = pos;
		};

		if (!listeners && scrollRef) {
			scrollRef.current.addEventListener("scroll", handleScroll);
			document.addEventListener("keydown", watchKeyDown);
			document.addEventListener("wheel", handleTrackpad, { passive: false });
			if (IE11) document.addEventListener("scroll", handleVerticalScroll);
			setListeners(true);
		}

		if (IE11) {
			if (
				(prevParts !== null && prevParts !== props.parts) ||
				(prevParameters && prevParameters !== props.parameters)
			) handleVerticalScroll();
		}
		handleHorizontalScroll();

		document.addEventListener('wheel', handleScroll, { passive: false });
		return () => {
			document.removeEventListener('wheel', handleScroll);
		};
	}, [
		props.parts,
		props.parameters,
		prevParts,
		prevParameters,
		listeners,
		KEY_LEFT,
		KEY_RIGHT,
		handleHorizontalScroll
	]);

	return (
		<>
			{
				FIREFOX && helpVisible
					? (
						<> 
							<div className="scroll__notification__backdrop"></div>
							<div className="scroll__notification">
								<div className="scroll__notification__title">
									Scroll Horizontally to See More
								</div>
								<div className="scroll__notification__content">
									Move your cursor to the bottom of the 
									table to reveal the scrollbar, then
									drag to view additional parameter columns
								</div>
								<button className="scroll__notification__button" onClick={ handleDismiss }>
									Dismiss
								</button>
								<div className="scroll__notification__pointer"></div>
							</div>
						</>
					)
					: null
			}
			<div ref={scrollRef} className="scroll">
				<div style={{ width: `${props.width}px` }} />
			</div>
		</>
		
	);
}

Scroll.defaultProps = {
	parameters: 0,
	parts: 0,
};

Scroll.propTypes = {
	parameters: PropTypes.array,
	parts: PropTypes.array,
	width: PropTypes.number,
};

const mapStateToProps = (state) => {
	let tempState = { width: state.table.width };

	if (IE11) {
		tempState.parts = state.parts.data.length;
		tempState.parameters = state.filters.parameters.length;
	}

	return tempState;
};

export default connect(mapStateToProps)(Scroll);
